import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { Icon, ICONS } from '@nimbus-library/features/shared/resources/icons';
import { MemberStateOption } from '@nimbus-global/features/reporting/models/member-state.model';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import {
  ITaskFlowHistogramRange,
  ITaskFlowLane,
  TASK_FLOW_LANE_HANDLED,
  TASK_FLOW_LANE_INCOMING,
  TaskFlowAgentKind,
  TaskFlowAgentNavigationTarget,
  TaskFlowBucketColor,
} from './task-flow.models';

/**
 * The fixed baseline lane that opens every flow. It is a total rather than an outcome group, so it is
 * not part of the bucket configuration and always leads the canvas.
 */
export const TASK_FLOW_FIXED_LANES: ITaskFlowLane[] = [
  {
    id: TASK_FLOW_LANE_INCOMING,
    // Labelled for the volume the service dealt with, inbound and outbound together; the lane that
    // collects the accepted outcomes is named for those instead, so the two do not read alike.
    labelKey: TranslationKeys.TaskFlowVisualizerLaneHandledTotal,
    hintKey: TranslationKeys.TaskFlowVisualizerLaneIncomingHint,
    color: TaskFlowBucketColor.Blue,
    filterable: false,
    isAgentLane: false,
    dimmed: false,
  },
];

/**
 * Built-in label per default bucket id. A bucket the user adds carries its own name instead, and a
 * default bucket falls back to its translated label until it is renamed.
 */
export const TASK_FLOW_DEFAULT_BUCKET_LABEL_KEYS: Record<string, string> = {
  hangUp: TranslationKeys.TaskFlowVisualizerLaneHangUp,
  failed: TranslationKeys.TaskFlowVisualizerLaneFailed,
  other: TranslationKeys.TaskFlowVisualizerLaneOther,
  lostAborted: TranslationKeys.TaskFlowVisualizerLaneLostAborted,
  [TASK_FLOW_LANE_HANDLED]: TranslationKeys.TaskFlowVisualizerLaneAccepted,
};

/**
 * How many drop-off buckets the canvas allows. The floor keeps the view meaningful — a single lane
 * says nothing about where volume is lost — and the ceiling keeps lanes wide enough to read and stays
 * within the colour palette.
 */
export const TASK_FLOW_MIN_BUCKETS = 3;

// Wide enough for a group row's controls and for the outcome chips to wrap without a scrollbar.
export const TASK_FLOW_BUCKET_DIALOG_WIDTH = '1080px';
export const TASK_FLOW_MAX_BUCKETS = 8;

// Keeps a group's label on one line in the lane header and the legend.
export const TASK_FLOW_BUCKET_NAME_MAX_LENGTH = 25;

/**
 * Sentinel for the "Today" window, which is not a fixed span: it runs from local midnight to now and so
 * grows through the day. Negative because every real window is a positive number of minutes, and the
 * value is resolved to actual minutes at read time rather than being stored as one.
 */
export const TASK_FLOW_TIME_WINDOW_TODAY = -1;

/**
 * Live window choices, shortest first, with Today last. The read API caps a window at 24 hours
 * (`MaximumSnapshotWindowMinutes`), which Today approaches late in the day but never exceeds.
 */
export const TASK_FLOW_TIME_WINDOWS_MINUTES: number[] = [15, 30, 60, 240, 480, TASK_FLOW_TIME_WINDOW_TODAY];
/**
 * What "All" asks for. The read API rejects anything above its own `MaximumTopAgentCount`, so this is
 * that cap rather than an arbitrary large number — a higher value is a 400, not more agents.
 */
export const TASK_FLOW_ALL_AGENTS = 50;
export const TASK_FLOW_TOP_N_OPTIONS: number[] = [2, 5, 8, 10, 15, 20, 25, TASK_FLOW_ALL_AGENTS];

/** A live view opens on the day so far, rather than on an hour that says nothing about the shift. */
export const TASK_FLOW_DEFAULT_TIME_WINDOW_MINUTES = TASK_FLOW_TIME_WINDOW_TODAY;

/** The read model returns at most five agents, so five is both the default and the useful maximum. */
export const TASK_FLOW_DEFAULT_TOP_N = 5;

/**
 * Live mode refreshes on SignalR deltas, debounced by this window so a burst of task completions
 * results in one re-read. The slower interval below is the safety net for a dropped connection.
 */
export const TASK_FLOW_REALTIME_DEBOUNCE_MS = 2000;

/**
 * What a live task's own length is divided by to get its crossing, in the same window-milliseconds-per-
 * real-millisecond terms a replayed window uses.
 *
 * A live delta arrives once the task has *ended*, so playing its length back at real time would draw a
 * dot for a call that finished minutes ago and leave it crawling for minutes more. Live is compressed
 * instead, exactly as a replayed window is: the differences between task lengths stay visible while
 * every dot still crosses in the couple of seconds the viewer is watching.
 */
export const TASK_FLOW_LIVE_PLAYBACK_RATE = 400;

/**
 * The ceiling on a live dot's crossing. Two things depend on it: a dot has to be off the canvas before
 * {@link TASK_FLOW_REALTIME_DEBOUNCE_MS} lets the re-read move the tallies, or the lane count and the
 * agent row would move before the dot that stands for the task got there; and the longest call of the
 * day must not park a dot on the canvas indefinitely.
 */
export const TASK_FLOW_LIVE_MAX_TRAVEL_MS = 1800;

/** How often the "updated N minutes ago" label re-reads the clock. */
export const TASK_FLOW_FRESHNESS_TICK_MS = 30000;
export const TASK_FLOW_LIVE_REFRESH_MS = 60000;

/**
 * LiveData caches its /live answer for a couple of seconds, per pod, so the read a push triggers can still
 * return the counts from before it. A second read this much later is past that cache.
 */
export const TASK_FLOW_LIVE_LANES_SETTLE_MS = 4000;
/** Re-reads the live lanes on this interval whatever SignalR does, so a missed push never leaves them stuck. */
export const TASK_FLOW_LIVE_LANES_POLL_MS = 30000;

/** Dots a NOW lane area draws before it collapses the rest into a "+N". */
export const TASK_FLOW_NOW_MAX_DOTS = 24;
/** Offset between neighbouring NOW dots' pulses, so an area shimmers rather than blinking in unison. */
export const TASK_FLOW_NOW_DOT_STAGGER_MS = 140;
/** Width range of the NOW lane in the left-to-right layout, set by dragging the divider on its edge. */
export const TASK_FLOW_NOW_LANE_MIN_WIDTH_PX = 112;
export const TASK_FLOW_NOW_LANE_MAX_WIDTH_PX = 320;
export const TASK_FLOW_DEFAULT_NOW_LANE_WIDTH_PX = 168;

/**
 * How often the selected service's member duty states are re-read. Not live — a duty toggle a viewer
 * catches within half a minute is plenty for this dot, and it is a single read of the whole roster
 * rather than a per-agent poll, so there is no cost pressure to go faster.
 */
export const TASK_FLOW_DUTY_STATE_REFRESH_MS = 30000;

/**
 * How often the selected service's "Hide User Statistics from Reporting" setting is re-read, so the
 * setting being switched on while the page is open takes effect without a reload.
 */
export const TASK_FLOW_VISIBILITY_REFRESH_MS = 60000;

/** The taskflows reads need the first, the overview's by-days read the second; either one missing is a 403. */
export const TASK_FLOW_REQUIRED_PERMISSIONS: NimbusPermission[] = [
  NimbusPermission.ReadServiceStatisticsReporting,
  NimbusPermission.ReadServiceHistoricalStatisticsReporting,
];

/** The Services Overview lists a service when the user holds either one, so the TFV never offers more than that page. */
export const TASK_FLOW_SERVICES_OVERVIEW_PERMISSIONS: NimbusPermission[] = [
  NimbusPermission.ReadConversationsInProgress,
  NimbusPermission.ReadServiceMembersState,
];

/** The TFV is for owners, supervisors and admins only; a plain member or agent of a service never gets it offered. */
export const TASK_FLOW_PERMISSION_SETS: NimbusPermissionSet[] = [
  NimbusPermissionSet.OuAdmin,
  NimbusPermissionSet.Owner,
  NimbusPermissionSet.OwnerLimited,
  NimbusPermissionSet.ParentOuAdmin,
  NimbusPermissionSet.ParentServiceSupervisor,
  NimbusPermissionSet.ServiceOwner,
  NimbusPermissionSet.ServiceSupervisor,
];

/**
 * Ranges of the drilldown's handled histogram. 30 days is the longest offered because that is the
 * Redis TimeSeries retention (`WorkflowVisualizerOptions.RetentionDays`).
 */
export const TASK_FLOW_HISTOGRAM_RANGES: ITaskFlowHistogramRange[] = [
  {
    id: 'today',
    labelKey: TranslationKeys.TaskFlowVisualizerHistogramRangeToday,
    minutes: 1440,
    bars: 24,
    spanLabelKey: TranslationKeys.TaskFlowVisualizerHistogramSpanToday,
  },
  {
    id: '48h',
    labelKey: TranslationKeys.TaskFlowVisualizerHistogramRange48H,
    minutes: 2880,
    bars: 24,
    spanLabelKey: TranslationKeys.TaskFlowVisualizerHistogramSpan48H,
  },
  {
    id: '7d',
    labelKey: TranslationKeys.TaskFlowVisualizerHistogramRange7D,
    minutes: 10080,
    bars: 28,
    spanLabelKey: TranslationKeys.TaskFlowVisualizerHistogramSpan7D,
  },
  {
    id: '30d',
    labelKey: TranslationKeys.TaskFlowVisualizerHistogramRange30D,
    minutes: 43200,
    bars: 30,
    spanLabelKey: TranslationKeys.TaskFlowVisualizerHistogramSpan30D,
  },
];

export const TASK_FLOW_DEFAULT_HISTOGRAM_RANGE_ID = '30d';

/**
 * The drilldown histogram while a window is replaying. It covers the window rather than one of the
 * ranges above — the ranges end at "now", which is not where a replayed day is — so it carries its
 * own id and its own bar count.
 */
export const TASK_FLOW_REPLAY_HISTOGRAM_RANGE_ID = 'replayWindow';

/**
 * The drilldown histogram in Live, which covers the page's own time window rather than a range of its
 * own. Carries its own id so the chart can tell that span apart from a replayed one.
 */
export const TASK_FLOW_WINDOW_HISTOGRAM_RANGE_ID = 'pageWindow';
export const TASK_FLOW_WINDOW_HISTOGRAM_BARS = 24;
export const TASK_FLOW_REPLAY_HISTOGRAM_BARS = 24;

/** The read API rejects a bucket larger than 60 minutes, so longer ranges aggregate client-side. */
export const TASK_FLOW_MAX_BUCKET_MINUTES = 60;

/**
 * Sentinel the Collector attributes a task to when no Nimbus user accepted it - a transfer out to a
 * phone number or to another service's resource account. Matches `UnassignedAgentValue` in
 * `RedisTaskFlowMetricsPublisher`. Resolving those targets to a real name is separate work; here it
 * only has to stop rendering as a raw literal.
 */
export const TASK_FLOW_UNASSIGNED_AGENT_ID = 'unassigned';

/**
 * Sentinel for a task the workflow recorded a voice message for. It is handled — the service dealt with
 * it — but no user was reached, so the Collector keys it separately from the target-less bucket rather
 * than letting a voicemail read as a task nobody got to. Matches `VoicemailAgentValue`.
 */
export const TASK_FLOW_VOICEMAIL_AGENT_ID = 'voicemail';

/**
 * How a row that belongs to no user is presented. Only the sentinels have an entry: a real agent renders
 * its own name and avatar, so an absent entry is what says "this row is somebody".
 */
/**
 * Rows that have no name of their own and are labelled instead. A service or a number is not here: the
 * read model sends its resource account or its number, and that is the only name it has, so the row shows
 * it rather than a generic word.
 */
export const TASK_FLOW_AGENT_KIND_LABEL_KEYS: Partial<Record<TaskFlowAgentKind, string>> = {
  [TaskFlowAgentKind.Unassigned]: TranslationKeys.TaskFlowVisualizerAgentUnassigned,
  [TaskFlowAgentKind.Voicemail]: TranslationKeys.TaskFlowVisualizerAgentVoicemail,
  [TaskFlowAgentKind.Unstated]: TranslationKeys.TaskFlowVisualizerAgentUnknown,
};

export const TASK_FLOW_NAVIGATION_LABEL_KEYS: Record<TaskFlowAgentNavigationTarget, string> = {
  [TaskFlowAgentNavigationTarget.Voicemails]: TranslationKeys.TaskFlowVisualizerAgentOpenVoicemails,
  [TaskFlowAgentNavigationTarget.Service]: TranslationKeys.TaskFlowVisualizerAgentOpenService,
};

/** Every kind but a person is iconed, so a row reads as what it is before its name is read. */
export const TASK_FLOW_AGENT_KIND_ICONS: Partial<Record<TaskFlowAgentKind, Icon>> = {
  [TaskFlowAgentKind.Unassigned]: ICONS.ANONYM_LIGHT,
  [TaskFlowAgentKind.Voicemail]: ICONS.VOICEMAIL,
  [TaskFlowAgentKind.Service]: ICONS.CALL_BUSINESS,
  [TaskFlowAgentKind.Pstn]: ICONS.PHONE_LIGHT,
  [TaskFlowAgentKind.Unstated]: ICONS.ANONYM_LIGHT,
};

/**
 * Nimbus's own duty state per member, collapsed onto the portal's duty colours. Sourced from team
 * membership (`MemberInfo.state`), not Microsoft Graph presence — the dot says whether the agent is on
 * duty and free to take work, not their Teams availability. Shared by the canvas' agent list and the
 * drilldown panel, so the same person reads the same colour in both. `NotAvailable` ("Not Ready") reads
 * amber like Teams' own away/not-ready treatment, not red — red is reserved for an actual busy call.
 */
export const TASK_FLOW_DUTY_STATE_CLASSES: Record<MemberStateOption, string> = {
  [MemberStateOption.Available]: 'presence-available',
  [MemberStateOption.NotAvailable]: 'presence-away',
  [MemberStateOption.Disabled]: 'presence-offline',
};

/**
 * Resolves a member's duty state to the dot's CSS class. `state` is `null`/`undefined` before the
 * roster has resolved this agent at all — or never will, for a sentinel row that is not a member —
 * in which case there is nothing to show and the dot stays off.
 */
export function toTaskFlowDutyStateClass(state: MemberStateOption | null | undefined): string {
  return (state && TASK_FLOW_DUTY_STATE_CLASSES[state]) || '';
}

/** Human label per duty colour, shown next to the dot beneath an agent's name. */
export const TASK_FLOW_DUTY_STATE_LABEL_KEYS: Record<string, TranslationKeys> = {
  'presence-available': TranslationKeys.GeneralDutyPresenceAvailable,
  'presence-away': TranslationKeys.GeneralDutyPresenceNotAvailable,
  'presence-offline': TranslationKeys.GeneralDutyPresenceOffDuty,
};

/** Metric name of the per-agent handled series in the timeseries read model. */
export const TASK_FLOW_AGENT_HANDLED_METRIC = 'agent_handled_count';

/** Metric name of the per-service inbound series — the task volume the overview plots per day. */
export const TASK_FLOW_SERVICE_INBOUND_METRIC = 'service_inbound_count';

/**
 * Metric name of the per-service call-on-behalf series. Reporting's per-slot conversation counts leave
 * calls placed on behalf of the service out, and no other outbound type, so this is what the scrubber
 * adds to a reporting-drawn day — and only this, or the outbound work reporting does count would double.
 */
export const TASK_FLOW_SERVICE_CALL_ON_BEHALF_METRIC = 'service_call_on_behalf_count';

/** Reporting keeps 15-minute slots, so the series added to them is read at the same resolution. */
export const TASK_FLOW_REPLAY_SLOT_MINUTES = 15;

/**
 * Day counts the task overview offers. The longest is the read model's retention, so a longer range
 * would only add empty bars rather than more history.
 */
export const TASK_FLOW_OVERVIEW_RANGES_DAYS: number[] = [7, 14, 30];
export const TASK_FLOW_DEFAULT_OVERVIEW_DAYS = 30;

/**
 * The overview reads hourly buckets and sums them per day: the endpoint caps bucketMinutes at 60, so
 * a daily bucket cannot be asked for directly. Thirty days is 720 points, which is a cheap read.
 */
export const TASK_FLOW_OVERVIEW_BUCKET_MINUTES = 60;

/**
 * Replay windows offered. The longest is bounded by the endpoint, which rejects a range beyond the
 * 30-day retention.
 */
export const TASK_FLOW_REPLAY_WINDOWS_MINUTES: number[] = [15, 60, 240, 1440];

/**
 * Playback is chosen as how long the window takes to watch, not as a multiple of real time. A
 * multiplier that suits fifteen minutes leaves a replayed day running for the best part of an hour,
 * so the clock derives its own multiplier from the window it was given.
 */
export const TASK_FLOW_REPLAY_PLAYBACK_SECONDS: number[] = [180, 60, 30];
export const TASK_FLOW_DEFAULT_REPLAY_PLAYBACK_SECONDS = 60;

/**
 * How long the canvas may sit empty before the clock jumps to the next task. A quiet service is
 * mostly dead air, and playing that back in proportion shows nothing at all.
 */
export const TASK_FLOW_REPLAY_MAX_IDLE_MS = 1500;

/** Matches the endpoint's page cap; one page is enough for the windows offered above. */
export const TASK_FLOW_REPLAY_PAGE_SIZE = 1000;

/**
 * The LiveData read cache only holds the retention window and is empty while Task Flow publishing is
 * switched off. Reporting keeps the same history for /sessions-list, so both the task overview and a
 * replayed day fall back to it.
 */
export const TASK_FLOW_REPORTING_TIMESTAMP_FILTER = 'timestamp';

/** Oldest first, so a replayed window arrives in event-time order. */
export const TASK_FLOW_REPORTING_ORDER_BY = 'timestamp+';

export const TASK_FLOW_MS_PER_SECOND = 1000;

/** How often the replay clock advances. Fast enough to look continuous, cheap enough to sustain. */
export const TASK_FLOW_REPLAY_TICK_MS = 100;

/**
 * How long the finished window is held before it starts again. The last tasks are still crossing the
 * canvas when the clock runs out, so rewinding straight away would zero the tallies underneath them.
 * Long enough after that to read the day's final figures, which is the point of the whole replay.
 */
export const TASK_FLOW_REPLAY_LOOP_PAUSE_MS = 10000;

export const TASK_FLOW_STORAGE_KEYS = {
  buckets: 'Nimbus.TaskFlowVisualizer.Dropoff.Buckets',
  timeWindowMinutes: 'Nimbus.TaskFlowVisualizer.Dropoff.TimeWindowMinutes',
  topN: 'Nimbus.TaskFlowVisualizer.Dropoff.TopN',
  nowLaneWidth: 'Nimbus.TaskFlowVisualizer.Dropoff.NowLaneWidth',
  orientation: 'Nimbus.TaskFlowVisualizer.Dropoff.Orientation',
  bucketFilterEnabled: 'Nimbus.TaskFlowVisualizer.Dropoff.BucketFilterEnabled',
  histogramRangeId: 'Nimbus.TaskFlowVisualizer.Drilldown.HistogramRange',
  overviewDays: 'Nimbus.TaskFlowVisualizer.Overview.Days',
  replayWindowMinutes: 'Nimbus.TaskFlowVisualizer.Replay.WindowMinutes',
  replayPlaybackSeconds: 'Nimbus.TaskFlowVisualizer.Replay.PlaybackSeconds',
  replayLoop: 'Nimbus.TaskFlowVisualizer.Replay.Loop',
};
